/**
 * ActivitiesTimelinePanel — reusable card showing the activity log
 * for a contact/company/deal. Filter by kind, log new activity inline.
 * Used by Company / Contact / Deal detail pages and the standalone
 * /activities page (without the regarding filter pre-filled).
 */

import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  useFiasLeadsActivities,
  useCreateFiasLeadsActivity,
  useDeleteFiasLeadsActivity,
} from '../hooks/useFiasLeads';
import type { FiasLeadsActivityKind, FiasLeadsActivityRegardingType } from '@/lib/api';

interface Props {
  holderId: string;
  regardingType: FiasLeadsActivityRegardingType;
  regardingId: string;
  limit?: number;
  allowLogNew?: boolean;
}

const KIND_OPTIONS: FiasLeadsActivityKind[] = ['note', 'call', 'email', 'meeting'];

const KIND_COLORS: Record<FiasLeadsActivityKind, string> = {
  note: 'bg-slate-100 text-slate-700',
  call: 'bg-blue-100 text-blue-700',
  email: 'bg-emerald-100 text-emerald-700',
  meeting: 'bg-purple-100 text-purple-700',
};

// `subject` is a required (NOT NULL) column but we no longer ask the user
// for it — derive a short headline from the description's first line so the
// data model stays satisfied and any subject-only display still reads well.
function deriveSubject(description: string): string {
  const firstLine = description.trim().split('\n')[0].trim();
  return (firstLine || description.trim()).slice(0, 120) || 'Activity';
}

// Current wall-clock time in the `datetime-local` format (no timezone) so
// the "When" field defaults to now while staying editable.
function nowLocalDatetime(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ActivitiesTimelinePanel({
  holderId,
  regardingType,
  regardingId,
  limit = 20,
  allowLogNew = true,
}: Props) {
  const [kindFilter, setKindFilter] = useState<FiasLeadsActivityKind | ''>('');
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<{
    kind: FiasLeadsActivityKind;
    description: string;
    // datetime-local string. Defaults to now (set when the form opens) and
    // is editable so a user can back-date "Sent follow-up email" yesterday.
    occurredAt: string;
  }>({ kind: 'note', description: '', occurredAt: '' });

  const activitiesQuery = useFiasLeadsActivities(holderId, {
    regardingType,
    regardingId,
    kind: kindFilter || undefined,
    limit,
  });
  const createActivity = useCreateFiasLeadsActivity(holderId);
  const deleteActivity = useDeleteFiasLeadsActivity(holderId);

  const openCreate = () => {
    setDraft({ kind: 'note', description: '', occurredAt: nowLocalDatetime() });
    setShowCreate(true);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const description = draft.description.trim();
    if (!description) return;
    await createActivity.mutateAsync({
      kind: draft.kind,
      regardingType,
      regardingId,
      subject: deriveSubject(description),
      bodyMarkdown: description,
      // datetime-local is wall-clock with no zone; convert to a real
      // instant. Cleared → omit so the server stamps now().
      occurredAt: draft.occurredAt ? new Date(draft.occurredAt).toISOString() : undefined,
    });
    setDraft({ kind: 'note', description: '', occurredAt: '' });
    setShowCreate(false);
  };

  const activities = activitiesQuery.data ?? [];

  return (
    <div className="rounded border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="text-sm font-medium text-muted-foreground">
          Activity ({activities.length})
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as FiasLeadsActivityKind | '')}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="">All kinds</option>
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          {allowLogNew && (
            <button
              onClick={() => (showCreate ? setShowCreate(false) : openCreate())}
              className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1 text-primary-foreground text-xs"
            >
              <Plus className="h-3 w-3" />
              Log activity
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="mb-3 rounded border border-border bg-background p-3 flex flex-col gap-2"
        >
          <select
            value={draft.kind}
            onChange={(e) =>
              setDraft((d) => ({ ...d, kind: e.target.value as FiasLeadsActivityKind }))
            }
            aria-label="Activity kind"
            className="w-fit rounded border border-border bg-card px-2 py-1 text-sm"
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Description</span>
            <textarea
              placeholder="What happened?"
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              rows={3}
              required
              autoFocus
              className="rounded border border-border bg-card px-3 py-2 text-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="shrink-0">When</span>
            <input
              type="datetime-local"
              value={draft.occurredAt}
              onChange={(e) => setDraft((d) => ({ ...d, occurredAt: e.target.value }))}
              aria-label="When this happened"
              className="rounded border border-border bg-card px-2 py-1 text-sm"
            />
            <span className="shrink-0">defaults to now</span>
          </label>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!draft.description.trim() || createActivity.isPending}
              className="rounded bg-primary px-3 py-1 text-primary-foreground text-xs disabled:opacity-50"
            >
              {createActivity.isPending ? 'Saving…' : 'Log'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-3 py-1 text-xs"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {activities.length === 0 ? (
        <p className="text-sm text-muted-foreground">No activity logged.</p>
      ) : (
        <ul className="divide-y divide-border">
          {activities.map((a) => (
            <li key={a.activityId} className="py-2 flex items-start gap-2 group">
              <span
                className={`shrink-0 mt-0.5 rounded px-2 py-0.5 text-xs uppercase ${KIND_COLORS[a.kind]}`}
              >
                {a.kind}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm whitespace-pre-wrap">{a.bodyMarkdown || a.subject}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(a.occurredAt).toLocaleString()}
                </p>
              </div>
              <button
                onClick={() => {
                  if (confirm('Delete this activity?')) {
                    deleteActivity.mutateAsync(a.activityId);
                  }
                }}
                className="opacity-0 group-hover:opacity-100 text-xs text-muted-foreground hover:text-destructive"
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
